import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import { Avatar, Box, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { updateApprovalLevel } from "api/services/approval-levels";
import { snack } from "components/toast";
import React, { ReactNode, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { formattedDatetime } from "utils/formattedDateTime";
import { handleError } from "utils/handleError";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LoadingButton from "components/LoadingButton";

interface Props {
  item: any;
  index: number;
  queryKey?: string;
  enabled: boolean;
  taskStatus?: string;
  levelBeforeStatus?: boolean;
}

function ApprovalLevel(props: Props) {
  const {
    item,
    index,
    enabled,
    queryKey = "task-approvals",
    taskStatus,
    levelBeforeStatus,
  } = props;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const params: any = useParams();
  const navigate = useNavigate();
  const [selectedIcon, setSelectedIcon] = useState(
    item?.rejectionArray?.length! >= 1 ? item?.rejectionArray?.length - 1 : null
  );
  useEffect(() => {
    setStatus(item?.status);
    setNewStatus(item?.status);
  }, [item]);

  const { mutate, isLoading } = useMutation(updateApprovalLevel, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(queryKey);
      if (res?.data["changeStatus"]) {
        navigate("/task-board");
      }

      snack.success("Updated successfully");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleUpdate = () => {
    mutate({
      approvalId: item.id,
      status: newStatus,
      taskId: params.taskId,
      remarks,
      level: item.name,
    });
  };

  const handleIconClick = (index) => {
    if (index === selectedIcon) {
      setSelectedIcon(index);
    } else {
      setSelectedIcon(index);
    }
  };

  const role = item?.assigneeRoles.replace(/^\d+/, "");
  const roleName = role.split(/(?=[A-Z])/).join(" ");

  const compareByCreateTime = (a, b) => {
    const createTimeA = new Date(a.createTime);
    const createTimeB = new Date(b.createTime);

    if (createTimeA < createTimeB) return -1;
    if (createTimeA > createTimeB) return 1;
    return 0;
  };

  // Sort the array using the comparison function
  item?.rejectionArray.sort(compareByCreateTime);

  // Output the sorted array

  const iconsArray = Array.from({ length: item?.rejectionArray?.length }, (_, index) => {
    let iconComponent: any;
    let iconBackgroundColor: any;
    let statusText: any;
    let iconSize = selectedIcon === index ? "30px" : "25px";

    if (item?.rejectionArray[index]?.status === "DECLINED") {
      iconBackgroundColor = "red";
      statusText = "Declined";
      iconComponent = <HighlightOffOutlinedIcon key={index} style={{ fontSize: iconSize }} />;
    } else if (item?.rejectionArray[index]?.status === "AUTO_DECLINED") {
      iconBackgroundColor = "orange";
      statusText = "Auto Declined";
      iconComponent = <DoNotDisturbOnOutlinedIcon key={index} style={{ fontSize: iconSize }} />;
    } else if (item?.rejectionArray[index]?.status === "APPROVED") {
      iconBackgroundColor = "green";
      statusText = "Approved";
      iconComponent = <CheckCircleOutlinedIcon key={index} style={{ fontSize: iconSize }} />;
    } else if (item?.rejectionArray[index]?.status === null) {
      iconBackgroundColor = "blue";
      statusText = "Pending";
      iconComponent = <HelpOutlineOutlinedIcon key={index} style={{ fontSize: iconSize }} />;
    }

    return (
      <Tooltip title={statusText} key={index}>
        <div
          key={index}
          onClick={() => handleIconClick(index)}
          style={{
            color: iconBackgroundColor,
            cursor: "pointer",
            margin: "1px",
            borderRadius: "50%",
          }}
        >
          {iconComponent}
        </div>
      </Tooltip>
    );
  });

  return (
    <TimelineItem
      sx={{
        "&:before": {
          display: "none",
        },
      }}
    >
      <TimelineSeparator>
        <TimelineDot
          variant="outlined"
          sx={{
            ...((item?.status === "APPROVED" ||
              (enabled && taskStatus === "under_review") ||
              (levelBeforeStatus && taskStatus === "under_review") ||
              (levelBeforeStatus && taskStatus !== "under_review" && index !== 0)) && {
              border: "2px solid 89B152",
            }),
          }}
        >
          {(item?.status === "APPROVED" ||
            (enabled && taskStatus === "under_review") ||
            (levelBeforeStatus && taskStatus === "under_review") ||
            (levelBeforeStatus && taskStatus !== "under_review" && index !== 0)) && (
              <HowToRegOutlinedIcon
                sx={{
                  color: "green",
                }}
              />
            )}
          {((item?.status === null && !enabled && !levelBeforeStatus) ||
            (item?.status === null && taskStatus !== "under_review" && index === 0)) && (
              <PersonOutlineOutlinedIcon
                sx={{
                  background: "white",
                }}
              />
            )}
        </TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent sx={{ pb: 5 }}>
        <Box
          sx={{
            background: "#f5f5f5",
            borderRadius: "5px",
            padding: "10px",
            width: "50%",
          }}
        >
          <Typography variant="subtitle2">Approval Level {index + 1}</Typography>
        </Box>
        <Box
          sx={{
            mt: 2,
            border: "1px solid #22222229",
            borderRadius: "5px",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid #22222229",
            }}
          >
            <Typography variant="h6">Reviewer Details</Typography>
          </Box>
          <Box p={2}>
            <Box display="flex" gap={1} alignItems="center">
              {item?.assigneeName ? (
                <Box flex={1} display="flex" gap={1} alignItems="center">
                  <Avatar src={item?.assigneeImageUrl} />
                  <Box>
                    <Typography variant="body1">{item?.assigneeName}</Typography>
                    <Typography variant="caption" color="rgba(0,0,0,0.5)">
                      {roleName}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box flex={1} display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    {item?.assigneeRoles.replace(/^\d+/, "")}
                  </Typography>
                </Box>
              )}
              <Box>
                <TextField
                  sx={{ width: 150 }}
                  label="Status"
                  variant="outlined"
                  size="small"
                  select
                  disabled={!enabled}
                  fullWidth
                  onChange={(e) => setNewStatus(e.target.value)}
                  value={newStatus}
                >
                  <MenuItem value="APPROVED">Approve</MenuItem>
                  <MenuItem value="DECLINED">Decline</MenuItem>
                  {taskStatus !== "under_review" && (
                    <MenuItem value="AUTO_DECLINED">AUTO_DECLINED</MenuItem>
                  )}
                </TextField>
              </Box>
            </Box>
            <Box mt={2}>
              <Typography variant="caption" color="rgba(0,0,0,0.5)">
                Last Updated on:
              </Typography>
              <Typography variant="body2">
                {/* {selectedIcon && item?.rejectionArray?.[selectedIcon]?.lastUpdatedOn} */}
                {selectedIcon !== null &&
                  item?.rejectionArray?.[selectedIcon]?.status !== "AUTO_DECLINED" &&
                  item?.rejectionArray?.[selectedIcon]?.status !== null
                  ? formattedDatetime(
                    selectedIcon !== null
                      ? item?.rejectionArray?.[selectedIcon]?.lastUpdatedOn
                      : null
                  )
                  : null}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  width: "280px",
                  overflowX: item?.rejectionArray?.length > 10 ? "auto" : "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {iconsArray}
              </Box>
            </Box>
            <Box mt={2}>
              <Typography variant="caption" color="rgba(0,0,0,0.5)">
                Remarks:
              </Typography>
              <Typography variant="body2">
                {selectedIcon !== null ? item?.rejectionArray?.[selectedIcon]?.comments : null}
              </Typography>
            </Box>
            {newStatus !== status && (
              <Box maxWidth={500} mt={3}>
                <TextField
                  placeholder="Remarks"
                  fullWidth
                  multiline
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  variant="outlined"
                />
                <Box display="flex" justifyContent="flex-start" mt={3} gap={2}>
                  <LoadingButton
                    onClick={handleUpdate}
                    loading={isLoading}
                    type="button"
                    loadingColor="white"
                    title="Update Changes"
                    color="secondary"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </TimelineContent>
    </TimelineItem>
  );
}

export default ApprovalLevel;
